import { ReactElement, useEffect } from 'react'
import { GetStaticPropsContext } from 'next'
import retrieveMessages, { LocaleType } from '../../messages/message'
import { NextPageWithLayout } from '../../common/common-types'
import PageLayout from '../../components/page-layout/page-layout'
import PdImage from '../../components/pd-image/pd-image'
import ContactSection from '../../components/pd-contactus-section/pd-contactus-section'
import { useChinaDetector } from '../../common/region-locale'
import { useCAPI } from '../../hooks/use-capi'
import { useRouter } from 'next/router'
import { withSEO } from '../../common/with-seo'
import { ContactUsSectionProps, getDefaultContactUsData } from '@/components/contact-us-section'
import { getContactUsData } from '@/service/contact-us.service'
import useSWR from 'swr'

interface ContactUsPageProps {
  contactUsData: ContactUsSectionProps['data'] | null
}

const ContactUs: NextPageWithLayout<ContactUsPageProps> = (props) => {
  const inChina = useChinaDetector()
  const router = useRouter()

  const locale = router.locale || 'en'

  const { data } = useSWR<ContactUsSectionProps['data']>(
    getContactUsData.swrkey(locale),
    () => getContactUsData(locale),
    {
      fallbackData: getDefaultContactUsData()
    }
  )

  // contact us event
  const trackContactUsConversionEvent = useCAPI('contactus')
  const contactUsData = data ?? getDefaultContactUsData()
  useEffect(() => {
    trackContactUsConversionEvent({ email: '', phone: '', name: '' })
  }, [trackContactUsConversionEvent])

  // 自动滚动到表单区域
  useEffect(() => {
    const { to } = router.query
    const hash = window.location.hash

    // 检查是否需要滚动到表单
    if (to === 'form' && hash === '#lead-form') {
      // 等待页面渲染完成后再滚动
      const timer = setTimeout(() => {
        const formElement = document.getElementById('contactUs')?.querySelector('#contactUs')
        if (formElement) {
          formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [router.query])

  return (
    <div className="bg-white">
      <div className={`w-full laptop:h-[40vh] mx-auto ${inChina ? 'h-250px' : 'h-50'}`}>
        <PdImage
          object="cover"
          objectPosition="center"
          src={
            inChina
              ? 'https://cdn.pudutech.com/official-website/banner/img_bg_contact.webp'
              : 'https://cdn.pudutech.com/official-website/banner/img_bg_contact.webp'
          }
          enableMask={false}
          useLoader={true}
          width={'100%'}
          height={'100%'}
        />
      </div>

      <div className={[, 'w-full mx-auto pb-40'].join(' ')}>
        <div className="laptop:flex-grow" id="contactUs">
          <ContactSection
            productSelectionMode={locale === 'zh-CN' ? 'dropdown' : 'inline'}
            includeIntendedProductTypes={locale !== 'zh-CN'}
            formOmitKeys={locale === 'zh-CN' ? [] : ['intendedProductType']}
            contactUsData={contactUsData}
            infoFooter={true}
          />
        </div>
      </div>
    </div>
  )
}
ContactUs.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}
export default ContactUs

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retrieveMessages(['contact-us'], locale as LocaleType)
    // 获取联系我们页面内容数据
    const contactUsData = await getContactUsData(locale || 'en').catch(() => null)
    return {
      props: {
        messages,
        contactUsData: contactUsData ?? null,
      },
    }
  },
  () => '/contact-us'
)
